import { Button, Typography, useTheme } from '@mui/material'
import { useUpdateProofOfRelease } from '@src/data/api/credit-application/credit-application-api'
import { useGetRequiredDocumentList } from '@src/data/api/document/document-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditApplication } from '../../../data/types'
import { CreditReport } from '../../../data/types/CreditReportSchema'
import ProofOfReleaseDialog from './proofOfReleaseDialog'
import ProofOfReleaseSummary from './proofOfReleaseSummary'

interface Props {
  creditApplication: CreditApplication
  applicantCreditReport: CreditReport | null
  coApplicantCreditReport: CreditReport | null
}

const ProofOfReleaseSection = ({ creditApplication, applicantCreditReport, coApplicantCreditReport }: Props) => {
  const [openProofOfReleaseDialog, setProofOfReleaseDialog] = useState<boolean>(false)
  const { t } = useTranslation()
  const theme = useTheme()
  const [updateProofOfReleaseHook] = useUpdateProofOfRelease()

  const [, , refetchRequiredDocuments] = useGetRequiredDocumentList(
    creditApplication.id,
    creditApplication.financingProgramId,
  )

  function proofOfReleaseRequired(data: Record<string, boolean>): string | undefined {
    const keys = Object.keys(data)
    if (keys.some((key) => data[key] === null)) {
      return 'proofMustBeSelected'
    }
    if (keys.some((key) => data[key] === true)) {
      return 'proofRequired'
    }
    return 'proofNotRequired'
  }

  const proofIsRequired = creditApplication.proofsOfRelease
    ? proofOfReleaseRequired(creditApplication.proofsOfRelease.releaseRequiredByLoanId)
    : undefined

  const keys = creditApplication.proofsOfRelease
    ? Object.keys(creditApplication.proofsOfRelease?.releaseRequiredByLoanId)
    : new Array<string>()
  const requiredProofOfRelease = keys.filter(
    (key) => creditApplication.proofsOfRelease.releaseRequiredByLoanId[key] === true,
  )

  const onCancelProofOfRelease = () => {
    setProofOfReleaseDialog(false)
  }

  const onConfirmProofOfRelease = async (data: Record<string, boolean>) => {
    const proofOfRelease = {
      ...creditApplication.proofsOfRelease,
      financingProgramId: creditApplication.financingProgramId,
      creditApplicationId: creditApplication.id,
      releaseRequiredByLoanId: data,
      versionTag: creditApplication.versionTag!,
    }
    setProofOfReleaseDialog(false)
    try {
      await updateProofOfReleaseHook(proofOfRelease)
      await refetchRequiredDocuments()
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  return (
    <>
      <Typography variant="h6" component="h3" gutterBottom>
        {t('proofsOfRelease.tittle')}
      </Typography>

      {proofIsRequired === 'proofMustBeSelected' && (
        <Typography
          variant="subtitle1"
          sx={{ backgroundColor: theme.palette.action.selected, marginBottom: 3 }}
          component="p"
        >
          {t('proofsOfRelease.proofOfReleaseMustBeSelected')}
        </Typography>
      )}

      {proofIsRequired === 'proofNotRequired' && (
        <Typography
          variant="subtitle1"
          sx={{ backgroundColor: theme.palette.action.selected, marginBottom: 3 }}
          component="p"
        >
          {t('proofsOfRelease.noProofOfReleaseRequired')}
        </Typography>
      )}

      {proofIsRequired === 'proofRequired' &&
        requiredProofOfRelease.map((accountNumber) => {
          return (
            <ProofOfReleaseSummary
              applicantCreditReport={applicantCreditReport}
              accountNumber={accountNumber}
              key={accountNumber}
            />
          )
        })}

      {Object.keys(creditApplication.proofsOfRelease.releaseRequiredByLoanId).length > 0 && (
        <Button
          variant="contained"
          size="large"
          sx={{
            mt: 2,
          }}
          onClick={() => setProofOfReleaseDialog(true)}
        >
          {t('proofsOfRelease.tittle')}
        </Button>
      )}

      <ProofOfReleaseDialog
        open={openProofOfReleaseDialog}
        applicantCreditReport={applicantCreditReport}
        coApplicantCreditReport={coApplicantCreditReport}
        onCancel={onCancelProofOfRelease}
        onConfirm={onConfirmProofOfRelease}
        title={t('proofsOfRelease.tittle')}
        releaseRequiredByLoanId={creditApplication.proofsOfRelease.releaseRequiredByLoanId}
      />
    </>
  )
}

export default ProofOfReleaseSection
